import { useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useHashRoute } from "@/hooks/useHashRoute";
import { useBootstrapStatus } from "@/hooks/useBootstrapStatus";
import { useAllowedUsers } from "@/hooks/useAllowedUsers";
import { rememberReturnTo } from "@/components/RequireAuth";
import { TopBar } from "@/components/TopBar";
import { UserList } from "@/components/admin/UserList";
import { AddUserForm } from "@/components/admin/AddUserForm";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function Admin() {
  const { loading: authLoading, session, user } = useAuth();
  const bootstrap = useBootstrapStatus();
  const { navigate } = useHashRoute();

  const isBootstrapMode = bootstrap.initialized === false;
  // Only redirect once we have a definitive answer from both auth and bootstrap.
  // If bootstrap errored, treat it as unknown — show an error instead of redirecting.
  const isReady = !authLoading && !bootstrap.loading;
  const bootstrapKnown = isReady && bootstrap.error === null;
  const allowedToView = isBootstrapMode || Boolean(session);

  const users = useAllowedUsers({ enabled: bootstrapKnown && allowedToView && !isBootstrapMode });

  useEffect(() => {
    if (!bootstrapKnown) return;
    // Only redirect when the system is initialized AND the user has no session.
    if (bootstrap.initialized === true && !session) {
      rememberReturnTo("#/admin");
      navigate("#/login");
    }
  }, [bootstrapKnown, bootstrap.initialized, session, navigate]);

  if (!isReady) {
    return (
      <div className="mx-auto mt-16 w-full max-w-xl space-y-3 px-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (bootstrap.error && !session) {
    return (
      <div className="mx-auto mt-16 w-full max-w-xl px-4">
        <Alert variant="destructive">
          <AlertDescription>
            Could not reach bootstrap status endpoint: {bootstrap.error}. Check that the Netlify functions are deployed and env vars are set.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!allowedToView) return null;

  const handleAdd = async (email: string) => {
    await users.add(email, isBootstrapMode);
    if (isBootstrapMode) bootstrap.refetch();
  };

  return (
    <div className="min-h-full">
      {!isBootstrapMode && <TopBar />}
      <main className="mx-auto w-full max-w-xl px-4 py-8 sm:px-6">
        {!isBootstrapMode && (
          <a
            href="#/"
            className="mb-6 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to control
          </a>
        )}

        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="text-lg font-semibold text-slate-100">Allowed users</h1>
          {!isBootstrapMode && (
            <span className="font-mono text-xs text-slate-500">
              {users.users.length} {users.users.length === 1 ? "member" : "members"}
            </span>
          )}
        </div>
        <div className="mb-6 h-px w-full bg-slate-800" />

        {isBootstrapMode ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                No users yet. Add the first allowed email to get started.
              </AlertDescription>
            </Alert>
            <AddUserForm onAdd={handleAdd} />
          </div>
        ) : (
          <>
            {users.loading && users.users.length === 0 ? (
              <Skeleton className="h-32 w-full" />
            ) : users.error ? (
              <Alert variant="destructive">
                <AlertDescription>{users.error}</AlertDescription>
              </Alert>
            ) : (
              <UserList
                users={users.users}
                currentEmail={user?.email ?? null}
                onRemove={users.remove}
              />
            )}

            <div className="mt-6">
              <AddUserForm onAdd={handleAdd} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
